import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { nestApi } from "@/lib/api";
import { ArrowLeft, KeyRound, Mail, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "" });

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1=email, 2=otp, 3=new password
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Please enter email and password");
      return;
    }
    setLoading(true);
    try {
      const { data } = await nestApi.post("/rahe-portal/auth/login", { email: form.email, password: form.password });
      if (data.is_first_login) {
        localStorage.setItem("rahe_show_onboarding", "true");
      }
      login(data.token, data.user);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.confirmPassword) {
      toast.error("Please fill all fields");
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await nestApi.post("/rahe-portal/auth/register", {
        name: form.name,
        email: form.email,
        password: form.password,
      });
      localStorage.setItem("rahe_show_onboarding", "true");
      login(data.token, data.user);
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSendOtp = async () => {
    if (!forgotEmail) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      const { data } = await nestApi.post("/rahe-portal/auth/forgot-password", { email: forgotEmail });
      toast.success("Verification code sent to your email");
      // For testing: show the OTP
      if (data.otp) toast.info(`Test OTP: ${data.otp}`, { duration: 10000 });
      setForgotStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotVerifyOtp = async () => {
    if (!forgotOtp || forgotOtp.length !== 6) { toast.error("Please enter the 6-digit code"); return; }
    setLoading(true);
    try {
      await nestApi.post("/rahe-portal/auth/verify-otp", { email: forgotEmail, otp: forgotOtp });
      setForgotStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async () => {
    if (!newPassword || newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (newPassword !== confirmNewPassword) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await nestApi.post("/rahe-portal/auth/reset-password", { email: forgotEmail, otp: forgotOtp, newPassword });
      toast.success("Password reset successfully! Please sign in.");
      resetForgotState();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const resetForgotState = () => {
    setForgotMode(false);
    setForgotStep(1);
    setForgotEmail("");
    setForgotOtp("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const renderForgotPassword = () => (
    <div className="space-y-5" data-testid="forgot-password-section">
      <button
        onClick={resetForgotState}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        data-testid="back-to-login"
      >
        <ArrowLeft className="w-4 h-4" /> Back to sign in
      </button>

      {forgotStep === 1 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Forgot Password</h3>
              <p className="text-xs text-slate-500">Enter your email to receive a verification code</p>
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Email</Label>
            <Input
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="your@email.com"
              className="mt-1.5"
              data-testid="forgot-email-input"
            />
          </div>
          <Button
            onClick={handleForgotSendOtp}
            disabled={loading}
            className="w-full bg-[#1A1B2E] hover:bg-[#2D2E45] h-11"
            data-testid="forgot-send-otp-btn"
          >
            {loading ? "Sending..." : "Send Verification Code"}
          </Button>
        </div>
      )}

      {forgotStep === 2 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Verify Code</h3>
              <p className="text-xs text-slate-500">Enter the 6-digit code sent to {forgotEmail}</p>
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Verification Code</Label>
            <Input
              value={forgotOtp}
              onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              className="mt-1.5 text-center text-lg tracking-[0.5em] font-mono"
              data-testid="forgot-otp-input"
            />
          </div>
          <Button
            onClick={handleForgotVerifyOtp}
            disabled={loading}
            className="w-full bg-[#1A1B2E] hover:bg-[#2D2E45] h-11"
            data-testid="forgot-verify-otp-btn"
          >
            {loading ? "Verifying..." : "Verify Code"}
          </Button>
        </div>
      )}

      {forgotStep === 3 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Set New Password</h3>
              <p className="text-xs text-slate-500">Choose a new password for your account</p>
            </div>
          </div>
          <div>
            <Label className="text-slate-700">New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 8 characters"
              className="mt-1.5"
              data-testid="forgot-new-password"
            />
          </div>
          <div>
            <Label className="text-slate-700">Confirm Password</Label>
            <Input
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              placeholder="Confirm new password"
              className="mt-1.5"
              data-testid="forgot-confirm-password"
            />
          </div>
          <Button
            onClick={handleForgotReset}
            disabled={loading}
            className="w-full bg-[#1A1B2E] hover:bg-[#2D2E45] h-11"
            data-testid="forgot-reset-btn"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Dark panel with logo */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1A1B2E] flex-col justify-between p-12">
        <div>
          <img src="/logo.svg" alt="Rahe Portal" className="h-10" />
        </div>
        <div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Welcome to Rahe Portal
          </h1>
          <p className="text-slate-400 text-lg">
            Your platform for local discovery experiences. Join our community of Local Discovery Guides.
          </p>
        </div>
        <div className="text-slate-500 text-sm">
          raheportal.com
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="bg-[#1A1B2E] p-4 rounded-xl">
              <img src="/logo.svg" alt="Rahe Portal" className="h-8" />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            {forgotMode ? (
              renderForgotPassword()
            ) : (
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="w-full bg-slate-100 p-1 rounded-xl mb-6">
                  <TabsTrigger
                    value="login"
                    className="flex-1 rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white"
                    data-testid="tab-login"
                  >
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger
                    value="register"
                    className="flex-1 rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white"
                    data-testid="tab-register"
                  >
                    Sign Up
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label className="text-slate-700">Email</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="your@email.com"
                        className="mt-1.5"
                        data-testid="email-input"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-slate-700">Password</Label>
                        <button
                          type="button"
                          onClick={() => setForgotMode(true)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
                          data-testid="forgot-password-link"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <Input
                        type="password"
                        value={form.password}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        placeholder="Enter password"
                        className="mt-1.5"
                        data-testid="password-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#1A1B2E] hover:bg-[#2D2E45] h-11"
                      data-testid="auth-submit-btn"
                    >
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label className="text-slate-700">Full Name</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="John Doe"
                        className="mt-1.5"
                        data-testid="name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Email</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="your@email.com"
                        className="mt-1.5"
                        data-testid="email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Password</Label>
                      <Input
                        type="password"
                        value={form.password}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        placeholder="Min 8 characters"
                        className="mt-1.5"
                        data-testid="password-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700">Confirm Password</Label>
                      <Input
                        type="password"
                        value={form.confirmPassword}
                        onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                        placeholder="Confirm password"
                        className="mt-1.5"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#1A1B2E] hover:bg-[#2D2E45] h-11"
                      data-testid="auth-submit-btn"
                    >
                      {loading ? "Creating account..." : "Create Account"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
